/**
 * R5Pagination
 * -----------------------------------------------------------
 * Number-button cluster with Prev/Next. 1-indexed.
 *
 * Props:
 *   page:          current page (1-indexed)
 *   pageCount:     total pages
 *   onPageChange:  (page) => void
 *   maxButtons:    max numbered buttons to show (default 7)
 */
import React from 'react';

function buildRange(page, total, max) {
  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const half = Math.floor(max / 2);
  let start = Math.max(1, page - half);
  let end = Math.min(total, start + max - 1);
  if (end - start + 1 < max) {
    start = Math.max(1, end - max + 1);
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function R5Pagination({
  page = 1,
  pageCount = 1,
  onPageChange,
  maxButtons = 7,
  className = '',
  ...rest
}) {
  if (pageCount <= 1) return null;
  const cls = ['r5-pagination', className].filter(Boolean).join(' ');
  const pages = buildRange(page, pageCount, maxButtons);
  const go = (n) => onPageChange && onPageChange(Math.max(1, Math.min(pageCount, n)));

  return (
    <nav className={cls} aria-label="Pagination" {...rest}>
      <button
        type="button"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        ← Prev
      </button>
      {pages.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => go(n)}
          className={n === page ? 'active' : ''}
          aria-current={n === page ? 'page' : undefined}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        onClick={() => go(page + 1)}
        disabled={page >= pageCount}
        aria-label="Next page"
      >
        Next →
      </button>
    </nav>
  );
}

export default R5Pagination;
